/* SVG has no native button element; focusable groups mirror the native connection-list buttons. */
/* oxlint-disable jsx-a11y/prefer-tag-over-role */
import { useId } from 'react';
import { Button } from '@signozhq/ui/button';
import { edgePath, NODE_HEIGHT, NODE_WIDTH } from './graphLayout';
import { useGraphCanvas } from './useGraphCanvas';
import type {
	GraphEdge,
	GraphNode,
	GraphPosition,
	GraphPositions,
} from './types';
import styles from './NodeGraphPanel.module.scss';

interface Props {
	nodes: GraphNode[];
	edges: GraphEdge[];
	positions: GraphPositions;
	selectedEdge?: string;
	selectedNode?: string;
	onSelectEdge: (id: string) => void;
	onSelectNode: (id: string) => void;
	onMove?: (id: string, position: GraphPosition) => void;
}

function GraphCanvas({
	nodes,
	edges,
	positions,
	selectedEdge,
	selectedNode,
	onSelectEdge,
	onSelectNode,
	onMove,
}: Props): JSX.Element {
	const canvas = useGraphCanvas(positions, onMove);
	const marker = useId().replace(/:/g, '');
	const { bounds, scale } = canvas;
	return (
		<div
			ref={canvas.container}
			className={styles.canvas}
			data-testid="node-graph-canvas"
		>
			<div className={styles.zoom}>
				<Button
					variant="ghost"
					color="secondary"
					size="sm"
					testId="node-graph-zoom-out"
					aria-label="Zoom out"
					onClick={() => canvas.setZoom(Math.max(0.01, scale - 0.2))}
				>
					−
				</Button>
				<Button
					variant="ghost"
					color="secondary"
					size="sm"
					testId="node-graph-fit"
					onClick={() => canvas.setZoom(undefined)}
				>
					Fit
				</Button>
				<Button
					variant="ghost"
					color="secondary"
					size="sm"
					testId="node-graph-zoom-reset"
					aria-label="Reset zoom to 100%"
					onClick={() => canvas.setZoom(1)}
				>
					{Math.round(scale * 100)}%
				</Button>
				<Button
					variant="ghost"
					color="secondary"
					size="sm"
					testId="node-graph-zoom-in"
					aria-label="Zoom in"
					onClick={() => canvas.setZoom(Math.min(2, scale + 0.2))}
				>
					+
				</Button>
			</div>
			<svg
				ref={canvas.svg}
				width={bounds.width * scale}
				height={bounds.height * scale}
				viewBox={`${bounds.x} ${bounds.y} ${bounds.width} ${bounds.height}`}
				role="group"
				aria-label="Directed dependency graph. Select a connection for details."
				preserveAspectRatio="xMidYMid meet"
			>
				<defs>
					<marker
						id={marker}
						viewBox="0 0 10 10"
						refX="9"
						refY="5"
						markerWidth="7"
						markerHeight="7"
						orient="auto-start-reverse"
					>
						<path d="M 0 0 L 10 5 L 0 10 z" fill="context-stroke" />
					</marker>
				</defs>
				{edges.map((edge) => (
					<g
						key={edge.id}
						role="button"
						tabIndex={0}
						aria-label={`${edge.scope}: ${edge.sourceName} calls ${edge.targetName}`}
						aria-pressed={selectedEdge === edge.id}
						data-tone={edge.tone}
						className={styles.edge}
						data-testid="node-graph-edge"
						onClick={() => onSelectEdge(edge.id)}
						onKeyDown={(event) => {
							if (event.key === 'Enter' || event.key === ' ') {
								event.preventDefault();
								onSelectEdge(edge.id);
							}
						}}
					>
						<path className={styles.edgeHit} d={edgePath(edge, positions)} />
						<path
							className={styles.edgeLine}
							d={edgePath(edge, positions)}
							markerEnd={`url(#${marker})`}
						/>
					</g>
				))}
				{nodes.map((node) => (
					<g
						key={node.id}
						transform={`translate(${positions[node.id].x},${positions[node.id].y})`}
						className={styles.node}
						role="button"
						tabIndex={0}
						aria-label={`${node.name}, ${node.scope}, ${node.group}`}
						aria-pressed={selectedNode === node.id}
						data-testid="node-graph-node"
						data-arranging={!!onMove}
						onClick={() => {
							if (!onMove) {
								onSelectNode(node.id);
							}
						}}
						onKeyDown={(event) => {
							const delta: Record<string, GraphPosition> = {
								ArrowLeft: { x: -16, y: 0 },
								ArrowRight: { x: 16, y: 0 },
								ArrowUp: { x: 0, y: -16 },
								ArrowDown: { x: 0, y: 16 },
							};
							if (onMove && delta[event.key]) {
								event.preventDefault();
								onMove(node.id, {
									x: positions[node.id].x + delta[event.key].x,
									y: positions[node.id].y + delta[event.key].y,
								});
								return;
							}
							if (event.key === 'Enter' || event.key === ' ') {
								event.preventDefault();
								onSelectNode(node.id);
							}
						}}
						onPointerDown={(event) => canvas.begin(node.id, event)}
						onPointerMove={(event) => canvas.move(node.id, event)}
						onPointerUp={canvas.end}
						onPointerCancel={canvas.end}
						onLostPointerCapture={canvas.end}
					>
						<title>
							{[node.name, node.scope, node.group].filter(Boolean).join(' / ')}
						</title>
						<rect width={NODE_WIDTH} height={NODE_HEIGHT} rx={8} />
						<text x={12} y={26} className={styles.nodeName}>
							{node.name.length > 23 ? `${node.name.slice(0, 22)}…` : node.name}
						</text>
						<text x={12} y={46} className={styles.nodeGroup}>
							{[node.scope, node.group].filter(Boolean).join(' / ').slice(0, 28)}
						</text>
					</g>
				))}
			</svg>
		</div>
	);
}

export default GraphCanvas;
